import { Analytics } from '@cobliteam/analytics';
import type { SessionUser } from '@cobliteam/common-types';
import { Location } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { useHistory } from 'react-router-dom';

interface AppUserProps {
  user: SessionUser;
  logout: () => void;
}

export default function AppUser({ user, logout }: AppUserProps) {
  const { t } = useTranslation('app-header');
  const history = useHistory();
  const { runtimeFlags } = useDashboard();
  const featureFlags = useFeatureFlags();

  const goToClientArea = () => {
    Analytics.track('UserButton - Navigate - ClientArea');
    history.push(Location.BUSINESS_MANAGER);
  };

  const goToFinance = () => {
    Analytics.track('UserButton - Navigate - Finance');
    history.push(Location.BUSINESS_MANAGER_BOLETOS);
  };

  const handleLogout = () => {
    Analytics.track('UserButton - Click - Logout');
    logout();
  };

  const goToMyAccount = () => {
    Analytics.track('UserButton - Navigate - MyAccount');
    history.push(Location.CONFIG_ACCOUNT);
  };

  const goToHelpCenter = () => {
    Analytics.track('NavBar - HelpCenter - Click');
    window.open('https://suporte.cobli.co/', '_blank', 'noopener,noreferrer');
  };

  const goToTrainings = () => {
    Analytics.track('UserButton - Navigate - Trainings');
    history.push(Location.TRAINING);
  };

  const goToSettings = () => {
    Analytics.track('UserButton - Navigate - Settings');
    history.push(Location.CONFIG_USERS);
  };

  const goToContact = () => {
    Analytics.track('UserButton - Navigate - Contact');
    window.open('https://suporte.cobli.co/contato-cobli', '_blank');
  };

  const goToCRM = () => {
    Analytics.track('UserButton - Navigate - Orders');
    history.push(Location.ORDERS);
  };

  return (
    <DropdownMenu
      onOpenChange={(isOpen) => {
        if (isOpen) {
          Analytics.track('UserButton - Open');
        }
      }}
    >
      <Tooltip content='Minha conta' asChild>
        <DropdownMenu.Trigger asChild>
          <div>
            <Button variant='surface' color='default' size='sm' data-testid='app-user' aria-label={t('user.menuAria')} className='size-8 rounded-full p-0'>
              <Icon name='user' size='md' />
            </Button>
          </div>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Content className='max-w-64'>
        <DropdownMenu.Label className='flex min-w-0 flex-col items-start gap-0 px-2 pt-1'>
          <span className='truncate font-medium text-gray-12 text-sm'>{user.name}</span>
          <span className='truncate font-normal text-gray-10 text-xs'>{user.email}</span>
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Group>
          <DropdownMenu.Item onClick={goToHelpCenter}>
            <DropdownMenu.Icon icon={<Icon name='circle-help' />} />
            <DropdownMenu.Label>{t('help.helpCenter')}</DropdownMenu.Label>
          </DropdownMenu.Item>

          <DropdownMenu.Item onClick={goToTrainings}>
            <DropdownMenu.Icon icon={<Icon name='graduation-cap' />} />
            <DropdownMenu.Label>{t('user.trainings')}</DropdownMenu.Label>
          </DropdownMenu.Item>

          <DropdownMenu.Item onClick={goToContact}>
            <DropdownMenu.Icon icon={<Icon name='headset' />} />
            <DropdownMenu.Label>{t('user.contact')}</DropdownMenu.Label>
          </DropdownMenu.Item>
        </DropdownMenu.Group>
        <DropdownMenu.Separator />
        {!runtimeFlags.noValidGroupOrVehicles && (
          <>
            <DropdownMenu.Group>
              {featureFlags.businessManager && (
                <DropdownMenu.Item onClick={goToClientArea}>
                  <DropdownMenu.Icon icon={<Icon name='building' />} />
                  <DropdownMenu.Label>{t('user.clientArea')}</DropdownMenu.Label>
                </DropdownMenu.Item>
              )}
              {featureFlags.businessManager && (
                <DropdownMenu.Item onClick={goToFinance}>
                  <DropdownMenu.Icon icon={<Icon name='dollar-sign' />} />
                  <DropdownMenu.Label>{t('user.finance')}</DropdownMenu.Label>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item onClick={goToCRM}>
                <DropdownMenu.Icon icon={<Icon name='shopping-cart' />} />
                <DropdownMenu.Label>{t('user.orders')}</DropdownMenu.Label>
              </DropdownMenu.Item>
            </DropdownMenu.Group>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={goToMyAccount}>
              <DropdownMenu.Icon icon={<Icon name='user' />} />
              <DropdownMenu.Label>{t('user.myAccount')}</DropdownMenu.Label>
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={goToSettings}>
              <DropdownMenu.Icon icon={<Icon name='settings' />} />
              <DropdownMenu.Label>{t('user.settings')}</DropdownMenu.Label>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        )}

        <DropdownMenu.Item onClick={handleLogout}>
          <DropdownMenu.Icon icon={<Icon name='log-out' />} />
          <DropdownMenu.Label>{t('user.logout')}</DropdownMenu.Label>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
